/**
 * Stamping configuration contracts (spec §7.6).
 *
 * The legacy system had one fixed set of coordinates per document type. This generalises
 * that to an ordered list of slots, because the real certificates in
 * `Reference_docs/Sample Stamped Certs/` do not agree on how many seals they carry or
 * where anything sits — an AANZFTA form and a JAEPA form are different documents.
 */
import { z } from 'zod';
import { stampSlotTypeSchema, textAlignSchema } from './enums.js';
import { pageKindSchema, slotPageKindSchema, templatePageKindsSchema } from './page-kinds.js';
/**
 * Coordinates are PDF points (72/inch) from the BOTTOM-LEFT of the page, matching
 * pdf-lib and the PDF specification itself. The placement UI works in top-left screen
 * space and converts once, at the boundary — converting in both directions throughout
 * the codebase is how stamps end up mirrored vertically.
 */
export const stampSlotSchema = z.object({
    id: z.string().optional(),
    slotType: stampSlotTypeSchema,
    label: z.string().trim().max(60).optional().nullable(),
    /** 1-based, matching what the operator sees in the preview. */
    page: z.number().int().min(1).default(1),
    /** Which pages of a real certificate it prints on (T-137). Derived by the server on save. */
    pageKind: slotPageKindSchema.default('FIXED'),
    x: z.number(),
    y: z.number(),
    width: z.number().positive().optional().nullable(),
    height: z.number().positive().optional().nullable(),
    rotation: z.number().min(-180).max(180).default(0),
    opacity: z.number().min(0).max(1).default(1),
    /** Text slots only. */
    fontFamily: z.string().max(60).default('Helvetica'),
    fontSize: z.number().min(4).max(72).default(10),
    fontColor: z.string().regex(/^#[0-9a-fA-F]{6}$/).default('#000000'),
    align: textAlignSchema.default('LEFT'),
    /** FREE_TEXT slots, and the fallback for a DATE slot's format. */
    staticText: z.string().max(200).optional().nullable(),
    dateFormat: z.string().max(30).default('DD/MM/YYYY'),
    /** SEAL slots: which library seal is pre-selected. Swappable at approval. */
    defaultSealId: z.string().optional().nullable(),
    /** Blocks approval if this slot resolves to nothing. */
    required: z.boolean().default(true),
    sortOrder: z.number().int().default(0),
});
export const stampingConfigSchema = z.object({
    id: z.string().optional(),
    documentTypeId: z.string(),
    name: z.string().trim().min(1).max(120),
    active: z.boolean().default(true),
    /** Page size the coordinates were captured against, for a sanity check at stamp time. */
    templatePageWidth: z.number().positive().optional().nullable(),
    templatePageHeight: z.number().positive().optional().nullable(),
    templatePageKinds: templatePageKindsSchema.optional().nullable(),
    stampOverleaf: z.boolean().default(false),
    slots: z.array(stampSlotSchema).max(40),
});
export const SUPPORTING_STAMP_POSITIONS = [
    'TOP_RIGHT',
    'TOP_CENTER',
    'BOTTOM_RIGHT',
    'BOTTOM_CENTER',
    'CUSTOM',
];
export const supportingStampPositionSchema = z.enum(SUPPORTING_STAMP_POSITIONS);
export const supportingDocumentStampConfigSchema = z.object({
    sealImageKey: z.string().nullable(),
    defaultPosition: supportingStampPositionSchema.default('BOTTOM_RIGHT'),
    defaultX: z.number().nullable().optional(),
    defaultY: z.number().nullable().optional(),
    width: z.number().positive().default(120),
    opacity: z.number().min(0).max(1).default(1),
    active: z.boolean().default(true),
});
export const sealSchema = z.object({
    id: z.string().optional(),
    name: z.string().trim().min(1).max(120),
    description: z.string().trim().max(500).optional().nullable(),
    imageKey: z.string(),
    active: z.boolean().default(true),
    validFrom: z.string().nullable().optional(),
    validTo: z.string().nullable().optional(),
});
/** A seal periodically re-registered with an authority is invalid outside its window. */
export function isSealValidOn(seal, on) {
    if (!seal.active)
        return false;
    const day = on.getTime();
    if (seal.validFrom && new Date(seal.validFrom).getTime() > day)
        return false;
    if (seal.validTo && new Date(seal.validTo).getTime() < day)
        return false;
    return true;
}
/** What the operator confirms in the Approve dialog, per slot. */
export const slotResolutionSchema = z.object({
    slotId: z.string(),
    sealId: z.string().nullable().optional(),
    /** One-off nudge, in points. Does NOT edit the saved configuration (spec §7.1). */
    offsetX: z.number().default(0),
    offsetY: z.number().default(0),
    textOverride: z.string().max(200).nullable().optional(),
});
export const supportingStampPlacementSchema = z.object({
    supportingDocumentId: z.string(),
    page: z.number().int().min(1).default(1),
    x: z.number(),
    y: z.number(),
    width: z.number().positive().optional(),
});
export const approveDocumentSchema = z.object({
    certificateDate: z.string(),
    signatureProfileId: z.string(),
    slotResolutions: z.array(slotResolutionSchema).default([]),
    supportingStampPlacements: z.array(supportingStampPlacementSchema).default([]),
    /**
     * Set when the operator approves with an unresolved AI flag showing. Recorded in the
     * audit trail, because "approved despite flagged mismatch X" is a legitimate and
     * visible outcome (spec §8.4), not something to hide.
     */
    acknowledgedFlags: z.array(z.string()).default([]),
    operatorNote: z.string().max(2000).optional().nullable(),
    /**
     * The operator's corrections to the page map, one entry per page (null = as detected).
     * Recorded with the certificate, so the stamping job prints exactly what they confirmed.
     */
    pageKinds: z.array(pageKindSchema.nullable()).max(60).optional().nullable(),
});
/**
 * What the Approve dialog sends to see the certificate before committing to it. The same
 * three inputs that change what gets drawn — everything else in an approval is a decision,
 * not a placement.
 */
export const stampPreviewSchema = approveDocumentSchema.pick({
    certificateDate: true,
    signatureProfileId: true,
    slotResolutions: true,
    pageKinds: true,
});
/**
 * Chamber-level choices about what happens when a certificate is issued.
 *
 * Deliberately a separate, tiny contract rather than a field on the session: an operator
 * who has been signed in all morning must see a change an administrator made at lunchtime
 * on the very next certificate, not after signing out.
 */
export const chamberIssuePreferencesSchema = z.object({
    /** Open the print dialog as soon as the stamped certificate is ready. Never the supporting documents. */
    printCertificateOnIssue: z.boolean(),
});
export const rejectDocumentSchema = z
    .object({
    rejectionReasonId: z.string().nullable(),
    freeText: z.string().trim().max(2000).optional().nullable(),
})
    .refine((v) => v.rejectionReasonId !== null || (v.freeText ?? '').trim().length > 0, {
    message: 'Choose a reason, or describe it under Other',
    path: ['rejectionReasonId'],
});
export const requestClarificationSchema = z
    .object({
    itemIds: z.array(z.string()).default([]),
    freeText: z.string().trim().max(4000).optional().nullable(),
    respondByDays: z.number().int().min(1).max(90).default(14),
})
    .refine((v) => v.itemIds.length > 0 || (v.freeText ?? '').trim().length > 0, {
    message: 'Pick at least one item, or write what you need',
    path: ['itemIds'],
});
/**
 * Who wanted a correction, and how they asked.
 *
 * The operator raising it is recorded separately; this is the question that was missing.
 * An upload or email exporter has no route to a correction through the product (D-016),
 * so they telephone — and nothing recorded that they had asked. Nobody could say how
 * often it happens, which is exactly what you would want to know before deciding whether
 * to build them a route.
 */
export const ModificationAskedBy = { EXPORTER: 'EXPORTER', CHAMBER: 'CHAMBER' };
export const ModificationAskedVia = {
    PHONE: 'PHONE',
    EMAIL: 'EMAIL',
    IN_PERSON: 'IN_PERSON',
    OTHER: 'OTHER',
};
export const raiseModificationSchema = z
    .object({
    documentId: z.string(),
    requestTypes: z.array(z.string()).default([]),
    comments: z.string().trim().min(1, 'Say what needs correcting').max(2000),
    askedBy: z.nativeEnum(ModificationAskedBy).default('CHAMBER'),
    askedVia: z.nativeEnum(ModificationAskedVia).nullable().optional(),
    /** Tell the exporter when this is processed. Only ever to an address on file. */
    notifyExporter: z.boolean().default(true),
})
    .refine((v) => v.askedBy !== 'EXPORTER' || Boolean(v.askedVia), {
    path: ['askedVia'],
    message: 'Say how they asked — it is the only record that they did',
});
export const revokeDocumentSchema = z.object({
    reason: z.string().trim().min(1, 'A revocation must say why').max(2000),
    /** Puts a replacement back in the queue, linked to the revoked original. */
    createReissue: z.boolean().default(false),
});
//# sourceMappingURL=stamping.js.map